/**
 * Helpers de acceso para staff/admin/viewer, desacoplados de React.
 *
 * El estado se sincroniza en localStorage desde `EditModeContext` (que lo
 * lee de Firestore `users/{uid}`). De esta forma, utilitarios que corren
 * fuera del árbol de React (o en guard clauses) pueden consultar permisos
 * sin depender del contexto.
 *
 * Claves de localStorage usadas:
 *   - userRole:         'admin' | 'staff' | 'viewer' | 'cashier' | 'qrScanner' | ''
 *   - isAdmin:          'true' | 'false'
 *   - allowedFeatures:  JSON array de ids de features (solo aplica a staff/viewer)
 */
import {
  ALL_STAFF_FEATURE_IDS,
  ADMIN_VIEW_TO_FEATURE,
  ROUTE_PATH_TO_FEATURE,
  canAccessAdminShellFromFeatures
} from '../config/staffFeatures';

const VALID_FEATURE_SET = new Set(ALL_STAFF_FEATURE_IDS);

function readRole() {
  try {
    return localStorage.getItem('userRole') || '';
  } catch (_) {
    return '';
  }
}

function readIsAdminFlag() {
  try {
    return localStorage.getItem('isAdmin') === 'true';
  } catch (_) {
    return false;
  }
}

function readAllowedFeatures() {
  try {
    const raw = localStorage.getItem('allowedFeatures');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

/** True si el usuario es administrador completo (rol 'admin'). */
export function isFullAdminFromStorage() {
  return readIsAdminFlag() || readRole() === 'admin';
}

/** True si el usuario es solo lectura (rol 'viewer'). */
export function isViewerFromStorage() {
  return readRole() === 'viewer';
}

/** True si el usuario es staff con accesos limitados. */
export function isStaffFromStorage() {
  return readRole() === 'staff';
}

/**
 * Devuelve true si el usuario tiene el feature habilitado.
 * Los admin siempre tienen true; staff/viewer dependen de allowedFeatures;
 * otros roles quedan fuera salvo que la feature esté en su lista.
 */
export function hasStaffFeature(featureId) {
  if (!featureId) return false;
  if (isFullAdminFromStorage()) return true;
  const feats = readAllowedFeatures();
  return feats.includes(featureId);
}

/**
 * Filtra un array de strings dejando solo ids de feature reconocidos.
 * Robusto frente a entradas no-array o con duplicados.
 */
export function filterValidStaffFeatureIds(input) {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  const result = [];
  for (const id of input) {
    if (typeof id !== 'string') continue;
    if (!VALID_FEATURE_SET.has(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
}

/** Persiste la lista de features permitidas para el usuario actual. */
export function persistAllowedFeatures(features) {
  try {
    const clean = filterValidStaffFeatureIds(features);
    localStorage.setItem('allowedFeatures', JSON.stringify(clean));
  } catch (_) {
    /* noop */
  }
}

/**
 * True si el usuario es staff o viewer con sesión válida (no admin completo).
 * Útil para mostrar UI de "panel restringido" sin tocar UI de admin total.
 */
export function isStaffOrViewerFromStorage() {
  if (isFullAdminFromStorage()) return false;
  try {
    if (localStorage.getItem('isAuthenticated') !== 'true') return false;
  } catch (_) {
    return false;
  }
  const role = readRole();
  return role === 'staff' || role === 'viewer';
}

/**
 * True si el usuario puede abrir el shell /admin.
 * Admin → siempre. Staff/viewer → si tienen al menos una feature válida.
 */
export function canAccessAdminShell() {
  return canAccessAdminShellFromFeatures(
    isFullAdminFromStorage(),
    readAllowedFeatures()
  );
}

/**
 * Gate por ruta "suelta" (no /admin). Devuelve true si el usuario tiene el
 * feature mapeado para ese pathname. Admin siempre pasa.
 *
 * Ejemplo: canAccessStaffPath('/gestion-stock') → true si tiene 'stockManagement'.
 */
export function canAccessStaffPath(pathname) {
  if (isFullAdminFromStorage()) return true;
  if (!pathname) return false;
  const feature = ROUTE_PATH_TO_FEATURE[pathname];
  if (!feature) return false;
  return hasStaffFeature(feature);
}

/**
 * Gate por subvista del AdminDashboard (?view=...) o pestaña interna.
 * Ej.: hasStaffFeatureForAdminView('customers') → requiere 'adminCustomers'.
 */
export function hasStaffFeatureForAdminView(viewKey) {
  const fid = ADMIN_VIEW_TO_FEATURE[viewKey];
  if (!fid) return false;
  return hasStaffFeature(fid);
}
